import { useEffect, useState } from 'react'
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { BloggerListQuery, BloggerStage, BloggerStageCounts } from '@ayub/shared'
import { BLOGGERS_PAGE_SIZE, fetchBloggers } from './api'
import type { BloggerListResponse } from './api'

/** "Hammasi" chipi — haqiqiy `BloggerStage` emas, filtrlanmagan holat. */
export const STAGE_ALL = 'ALL' as const
export type StageFilter = typeof STAGE_ALL | BloggerStage

/** Qidiruv maydonini debounce qiladi — har bosilgan tugma uchun so'rov yubormaslik uchun. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

interface UseBloggerListParams {
  stage: StageFilter
  q: string
  page: number
  sort: NonNullable<BloggerListQuery['sort']>
  order: NonNullable<BloggerListQuery['order']>
}

/** List ko'rinishi uchun sahifalangan, server tomonida saralangan so'rov. */
export function useBloggerList({ stage, q, page, sort, order }: UseBloggerListParams) {
  const query = useQuery<BloggerListResponse, Error>({
    queryKey: ['bloggers', 'list', stage, q, page, sort, order],
    queryFn: () =>
      fetchBloggers({
        stage: stage === STAGE_ALL ? undefined : stage,
        q: q.length > 0 ? q : undefined,
        page,
        pageSize: BLOGGERS_PAGE_SIZE,
        sort,
        order,
      }),
    placeholderData: keepPreviousData,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

/**
 * Filtr chiplari va kanban ustun sarlavhalari uchun har bir bosqichdagi umumiy son.
 *
 * Server buni HAR QANDAY filtrdan mustaqil hisoblaydi (`api.ts`dagi izohga qarang) —
 * shuning uchun bu yerda `q`/`stage` argumenti YO'Q, bitta umumiy so'rov kifoya va u
 * butun sahifa bo'ylab keshlanadi (`queryKey` doim bir xil).
 */
export function useBloggerStageCounts() {
  const query = useQuery<BloggerStageCounts, Error>({
    queryKey: ['bloggers', 'stageCounts'],
    queryFn: async () => {
      const response = await fetchBloggers({ page: 1, pageSize: 1 })
      return response.stageCounts
    },
    placeholderData: keepPreviousData,
  })

  return {
    counts: query.data,
    isLoading: query.isLoading,
    error: query.isError ? query.error : null,
  }
}

interface UseBloggerColumnResult {
  items: BloggerListResponse['items']
  total: number
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  hasMore: boolean
  loadMore: () => void
}

/**
 * Kanban ustuni uchun ma'lumot — `docs/04-ui-ux.md`: "Ustun ichida vertikal skroll,
 * 50 tadan keyin 'yana yuklash'".
 *
 * `useInfiniteQuery` bilan haqiqiy sahifama-sahifa yuklash: server `pageSize`ni 100 bilan
 * cheklaydi (`MAX_PAGE_SIZE`, `services/bloggers.ts`), shuning uchun "har safar kattaroq
 * bitta sahifa so'rash" usuli ustunlar 100 tadan oshganda 400 xatosiga uchraydi. Sahifalarni
 * to'plab borish bu muammoni yo'q qiladi va standart, to'g'ri naqsh.
 */
export function useBloggerColumn(stage: BloggerStage, q: string): UseBloggerColumnResult {
  const query = useInfiniteQuery<BloggerListResponse, Error>({
    queryKey: ['bloggers', 'kanban', stage, q],
    queryFn: ({ pageParam }) =>
      fetchBloggers({
        stage,
        q: q.length > 0 ? q : undefined,
        page: pageParam as number,
        pageSize: BLOGGERS_PAGE_SIZE,
        sort: 'createdAt',
        order: 'desc',
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  const items = query.data ? query.data.pages.flatMap((p) => p.items) : []
  const total = query.data?.pages[0]?.total ?? 0

  return {
    items,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching || query.isFetchingNextPage,
    error: query.isError ? query.error : null,
    hasMore: query.hasNextPage ?? false,
    loadMore: () => void query.fetchNextPage(),
  }
}
